/**
 * Navbar Component
 * 
 * Main navigation bar with responsive menu functionality.
 * 
 * Features:
 * - Sticky navigation with company branding
 * - Desktop dropdown menu with hover effects
 * - Mobile hamburger menu
 * - Smooth scroll navigation to page sections
 * - Accessible keyboard navigation
 * 
 * @component
 * @example
 * ```tsx
 * <Navbar />
 * ```
 */

import { memo, useState, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { navLinkClass } from "@/utils/cn";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);
  
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);
  
  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);
  
  return (
    <>
      {/* Dark overlay for mobile menu */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />
    <nav 
      className={`absolute top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHomePage && !mobileMenuOpen 
          ? '' 
          : 'bg-white border-b border-border/50 backdrop-blur-sm'
      }`}
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            className="hover:opacity-80 transition-opacity"
            aria-label="OceanNova home"
          >
            <img 
              src="/assets/oceannova-horizontal-logo.png" 
              alt="OceanNova - Global Shipping & Freight Forwarding" 
              className="h-16 md:h-20 w-auto"
              width="200"
              height="32"
            />
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6" role="navigation">
            <Link to="/" className={navLinkClass(isHomePage)}>
              Home
            </Link>
            <div className="relative group">
              <button 
                className={`${navLinkClass(isHomePage)} flex items-center gap-1`}
                aria-haspopup="true"
                aria-expanded="false"
              >
                Services
                <svg 
                  className="w-4 h-4 transition-transform group-hover:rotate-180" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div 
                className="absolute top-full left-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50"
                role="menu"
              >
                <div className="py-2">
                  <Link to="/services/port-ship-agency" className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors">
                    Port & Ship Agency
                  </Link>
                  <Link to="/services/ship-chartering" className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors">
                    Ship Chartering
                  </Link>
                  <Link to="/services/project-cargo" className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors">
                    Project Cargo & Consulting
                  </Link>
                  <Link to="/services/rail-road-logistics" className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors">
                    Rail & Road Logistics
                  </Link>
                  <Link to="/services/terminal-port-development" className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors">
                    Terminal & Port Development
                  </Link>
                  <Link to="/services/air-freight" className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors">
                    Air Freight
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/our-story" className={navLinkClass(isHomePage)}>
              Our Story
            </Link>
            <Link to="/contact" className={navLinkClass(isHomePage)}>
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className={`md:hidden ${
              isHomePage && !mobileMenuOpen 
                ? 'text-white hover:bg-white/10' 
                : 'text-foreground hover:bg-muted'
            }`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <MenuIcon className="h-6 w-6" aria-hidden="true" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen 
              ? 'max-h-[800px] opacity-100' 
              : 'max-h-0 opacity-0'
          }`}
          role="navigation"
          aria-label="Mobile navigation menu"
        >
          <div className="pb-4 pt-6 mt-6 border-t border-border">
            <nav className="flex flex-col space-y-6">
              <Link 
                to="/" 
                className="text-foreground font-semibold hover:text-primary transition-colors"
                onClick={closeMobileMenu}
              >
                Home
              </Link>
              <div>
                <span className="text-foreground font-semibold mb-3 block">Services</span>
                <div className="flex flex-col space-y-3 pl-4">
                  <Link to="/services/port-ship-agency" className="text-muted-foreground hover:text-accent transition-colors" onClick={closeMobileMenu}>
                    Port & Ship Agency
                  </Link>
                  <Link to="/services/ship-chartering" className="text-muted-foreground hover:text-accent transition-colors" onClick={closeMobileMenu}>
                    Ship Chartering
                  </Link>
                  <Link to="/services/project-cargo" className="text-muted-foreground hover:text-accent transition-colors" onClick={closeMobileMenu}>
                    Project Cargo & Consulting
                  </Link>
                  <Link to="/services/rail-road-logistics" className="text-muted-foreground hover:text-accent transition-colors" onClick={closeMobileMenu}>
                    Rail & Road Logistics
                  </Link>
                  <Link to="/services/terminal-port-development" className="text-muted-foreground hover:text-accent transition-colors" onClick={closeMobileMenu}>
                    Terminal & Port Development
                  </Link>
                  <Link to="/services/air-freight" className="text-muted-foreground hover:text-accent transition-colors" onClick={closeMobileMenu}>
                    Air Freight
                  </Link>
                </div>
              </div>
              <Link 
                to="/our-story" 
                className="text-foreground font-semibold hover:text-primary transition-colors"
                onClick={closeMobileMenu}
              >
                Our Story
              </Link>
              <Link 
                to="/contact" 
                className="text-foreground font-semibold hover:text-primary transition-colors"
                onClick={closeMobileMenu}
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default memo(Navbar);
